"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/Button";
import { Container } from "@/components/Container";
import Logo from "@/components/Logo";
import { Particles } from "@/components/Particles";
import clsx from "@/helpers/clsx";
import { twc } from "@/helpers/twc";
import imageFeatureAnalytics from "@/public/_static/images/feature-analytics.png";
import imageFeatureFund from "@/public/_static/images/feature-fund.png";
import imageFeatureInbox from "@/public/_static/images/feature-inbox.png";
import imageReporting from "@/public/_static/images/feature-reporting.png";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { PauseIcon } from "@heroicons/react/24/solid";
import { motion, useInView, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import React, { useRef, useState } from "react";
import { AnalyticsIcon, FounderUpdatesIcon, FundsIcon, PortfolioIcon, ReportingIcon } from "./FeatureIcons";
import { GridBackground } from "./GridBackground";
import { LogoTicker } from "./LogoTicker";
const MotionImage = motion(Image);
export function PrimaryFeatures(t0) {
  const $ = _c(21);
  const {
    logos
  } = t0;
  const ref = useRef();
  const isInView = useInView(ref);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute left-0 right-0 top-0 h-px bg-[linear-gradient(90deg,transparent_20%,rgba(112,112,112,0.4)_50%,transparent_80%,rgb(46,46,46,0)_100%)]" />;
    t2 = <div className="fundra-bg h-[800px] opacity-20 [background-size:min(100vw,1000px),200px]" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isInView) {
    t3 = isInView && <Particles className="fade-vertical absolute inset-x-0 top-0 mx-auto flex h-[420px] w-[300px] overflow-hidden [--color:#d1fae5] [contain:strict]" />;
    $[2] = isInView;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FeatureTitles className="hidden md:flex"><span>Monitor your portfolio</span><span className="dot">•</span><span>Make investment decisions</span><span className="dot">•</span><span>Automate LP reporting</span></FeatureTitles>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FeatureTitles className="flex md:hidden"><span>Monitor portfolio</span><span className="dot">•</span><span>Make decisions</span><span className="dot">•</span><span>Automate reporting</span></FeatureTitles>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mx-auto flex max-w-2xl flex-col text-center xl:max-w-none">{t4}{t5}<h2 className="font-fundra text-[28px] font-bold tracking-[-0.1px] text-white sm:text-4xl md:text-5xl">Everything you need<span className="block h-0" />to run your venture fund</h2><p className="mx-auto mt-4 max-w-lg text-lg tracking-tight text-gray-400 md:mt-6 md:text-xl">{"Your single source of truth about your portfolio, funds, and LP & founder reporting. All in one place, for ultimate data-driven portfolio management."}</p></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== logos) {
    t7 = <LogoTicker logos={logos} />;
    $[7] = logos;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Feature id="portfolio-monitoring" Icon={<PortfolioIcon />} title="Portfolio monitoring" description="Track your portfolio companies and fund investments. Our robust portfolio data model makes sure that your data is always correct and trustworthy." video="https://static-assets.fundra.com/video-portfolio-v2.mp4" />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Feature id="fund-analytics" Icon={<FundsIcon />} title="Fund Management" description="Track your deployment rate, LPs, key fund-level performance indicators, construct your portfolio, and more." poster={imageFeatureFund} />;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Feature id="investor-updates" Icon={<FounderUpdatesIcon />} title={<>Investor updates</>} description={"Receive investor updates, KPIs, board materials, etc. \u2013 all in the Fundra app. Designed with a first-in-class founder experience. Save hours of manual work and streamline portfolio data access for your entire team. Everything is automatically categorised, indexed and summarised for easy retrieval."} poster={imageFeatureInbox} />;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ReportingIcon />;
    t12 = <>LP reporting</>;
    $[12] = t11;
    $[13] = t12;
  } else {
    t11 = $[12];
    t12 = $[13];
  }
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Feature id="lp-reporting" Icon={t11} title={t12} description={"Create LP reports in just a few clicks using your portfolio data and founder updates \u2013 powered by AI. Save days of manual work, eliminate errors, and turn LP reporting from a chore into a powerful marketing asset."} poster={imageReporting} children={<FeatureLPReporting />} />;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="md:[&>div:nth-child(2n)_.feature]:flex-row-reverse md:[&>div:nth-child(2n)_.line]:scale-x-[-1]">{t8}{t9}{t10}{t13}<Feature id="analytics" Icon={<AnalyticsIcon />} title={<>Powerful analytics</>} description={"Our powerful filtering and aggregation features makes it easy to pull off ad-hoc analyses across all your funds and companies \u2013 make data driven decisions without spending hours on building spreadsheets."} poster={imageFeatureAnalytics} /></div>;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t7) {
    t15 = <Container className="relative">{t6}{t7}{t14}</Container>;
    $[16] = t7;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== t15 || $[19] !== t3) {
    t16 = <section id="features" aria-label="Features for running your fund" className="relative z-10 bg-body pb-28 pt-20 sm:py-24" ref={ref}>{t1}{t2}{t3}{t15}</section>;
    $[18] = t15;
    $[19] = t3;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  return t16;
}
const FeatureLPReporting = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [step, setStep] = useState(0);
  const [icons, setIcons] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [text, setText] = useState("");
  const buttonRef = useRef(null);
  const timeoutsRef = useRef([]);
  const intervalRef = useRef(null);
  const cleanup = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsGenerating(false);
    setStep(0);
    setIcons([]);
    setNumbers([]);
    setText("");
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return cleanup;
  }, []);
  const startGenerating = () => {
    cleanup(); // Clear any existing timers
    setIsGenerating(true);
    setStep(0);
    // Step 1: Reading updates
    const newIcons = Array.from({
      length: 10
    }, (_, i) => ({
      id: i
    }));
    setIcons(newIcons);

    // Schedule all the steps
    timeoutsRef.current = [setTimeout(() => {
      setStep(1);
      setIcons([]);
      // Create fixed positions for 20 numbers
      const fixedNumbers = Array.from({
        length: 20
      }, () => ({
        id: Math.random(),
        value: (Math.random() * 100).toFixed(2),
        prefix: Math.random() > 0.5 ? "+" : "",
        label: Math.random() > 0.5 ? "%" : "x"
      }));
      setNumbers(fixedNumbers);

      // Animate number values
      const numbersInterval = setInterval(() => {
        setNumbers(prev => prev.map(n => ({
          ...n,
          value: (Math.random() * 100).toFixed(2)
        })));
      }, 300);
      timeoutsRef.current.push(setTimeout(() => clearInterval(numbersInterval), 2900));
    }, 3000), setTimeout(() => {
      setStep(2);
      setNumbers([]);
      setText("");
      const summary = "Q2 2025 showed strong performance across the portfolio with 3 up-rounds and 1 exit. The fund's TVPI improved to 2.8x.";
      let i_0 = 0;
      intervalRef.current = setInterval(() => {
        setText(summary.slice(0, i_0));
        i_0++;
        if (i_0 > summary.length) clearInterval(intervalRef.current);
      }, 30);
    }, 6000), setTimeout(() => {
      setStep(3);
    }, 10000), setTimeout(cleanup, 14000)];
  };
  return <div className={clsx("absolute inset-0 flex items-end justify-center overflow-hidden pb-8 transition-all", isGenerating && "bg-black/20 backdrop-blur-sm")}>
      {true && <div className="absolute inset-0 flex items-center justify-center">
          {/* Step 1: Reading updates */}
          {step === 0 && <div className="relative h-12">
              <div className="relative h-full w-full">
                {icons.map(icon => <motion.div key={icon.id} className="absolute left-1/2 top-0" initial={{
            x: "-200px",
            opacity: 0
          }} animate={{
            x: "200px",
            opacity: [0, 1, 1, 0]
          }} transition={{
            x: {
              duration: 3,
              delay: icon.id * 0.8,
              ease: "linear"
            },
            opacity: {
              duration: 3,
              delay: icon.id * 0.8,
              ease: "linear"
            }
          }}>
                    <div className="relative origin-center overflow-hidden rounded-xl">
                      <FounderUpdatesIcon className="size-12 text-primary-400/80" />
                      <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" animate={{
                x: ["-100%", "100%"]
              }} transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "linear"
              }} />
                    </div>
                  </motion.div>)}
              </div>
            </div>}

          {/* Step 2: Numbers */}
          {step === 1 && <div className="grid grid-cols-5 gap-3 px-8">
              {numbers.map(num => <motion.div key={num.id} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="font-mono text-xs tabular-nums text-primary-400 md:text-sm">
                  <motion.span key={num.value} initial={{
            opacity: 0.5,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }}>
                    {num.prefix}
                    {num.value}
                    {num.label}
                  </motion.span>
                </motion.div>)}
            </div>}

          {/* Step 3: Summary */}
          {step === 2 && <div className="px-8">
              <motion.div className="mx-auto max-w-md text-lg font-semibold leading-[1.7] text-body" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }}>
                {text}
                <motion.span animate={{
            opacity: [1, 0]
          }} transition={{
            duration: 0.8,
            repeat: Infinity
          }} className="text-primary-400">
                  |
                </motion.span>
              </motion.div>
            </div>}

          {/* Step 4: Report */}
          {step === 3 && <div className="z-10 flex size-full flex-1 transform-gpu animate-fadeIn flex-col items-center bg-black/50 px-8 py-8 backdrop-blur-lg animate-duration-1000">
              <Logo className="h-8" />
              <h1 className="mx-auto my-auto max-w-xs text-balance text-center text-3xl">
                Fundra IV Capital Partners LP
              </h1>
              <p className="mb-1 text-sm text-primary">Quarterly Report</p>
              <h3 className="text-center font-fundra text-lg font-bold tracking-tight text-white sm:text-2xl">
                Q1 2025
              </h3>
            </div>}
        </div>}

      <Button ref={buttonRef} onClick={() => {
      if (!isGenerating) {
        startGenerating();
      } else {
        cleanup();
      }
    }} className={clsx("border border-white/10 bg-white/10 bg-none text-[13px] shadow-md backdrop-blur-lg", isGenerating && "animate-twPulse animate-infinite")}>
        <SparklesIcon className="mr-2 size-4 text-primary-400" />
        {isGenerating ? step === 0 ? <>Reading investor updates...</> : step === 1 ? <>Crunching the quarterly numbers...</> : step === 2 ? <>Writing executive summary...</> : <>Report done!</> : <>Generate a report</>}
      </Button>
    </div>;
};
const Feature = ({
  id,
  Icon,
  title,
  description,
  video,
  poster,
  children
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const imageRef = useRef(null);
  const isInView = useInView(playerRef, {
    once: true,
    amount: "some"
  });
  React.useEffect(() => {
    if (!poster) return;
    const handleMouseMove = e => {
      const rect = imageRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      setMousePosition({
        x,
        y
      });
    };
    const handleMouseLeave = () => {
      setMousePosition({
        x: 0,
        y: 0
      });
      setIsHovering(false);
    };
    const handleMouseEnter = () => {
      setIsHovering(true);
    };
    const container = imageRef.current;
    container?.addEventListener("pointermove", handleMouseMove);
    container?.addEventListener("pointerleave", handleMouseLeave);
    container?.addEventListener("pointerenter", handleMouseEnter);
    return () => {
      container?.removeEventListener("pointermove", handleMouseMove);
      container?.removeEventListener("pointerleave", handleMouseLeave);
      container?.removeEventListener("pointerenter", handleMouseEnter);
    };
  }, [poster]);
  React.useEffect(() => {
    if (!isInView) return;
    try {
      playerRef.current?.play();
    } catch (error) {
      console.error("Error playing video:", error);
    }
  }, [isInView]);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });
  const distance = [0.15, 0.25];
  const y_0 = useTransform(scrollYProgress, distance, [70, 0]);
  const opacity = useTransform(scrollYProgress, distance, [0, 1]);
  return <div id={id} className="group mt-24 md:mt-32">
      <div className={clsx("feature relative flex flex-col-reverse gap-x-8 gap-y-8 px-0 sm:px-0 md:flex-row")}>
        <div className="relative flex-1">
          <div className="bg-btn-secondary relative z-[2] aspect-square w-full flex-1 overflow-hidden rounded-md border-[0.5px] border-transparent shadow-circle shadow-black/20 md:rounded-lg">
            <div className="absolute -top-px left-0 right-0 h-[1.5px] bg-[linear-gradient(90deg,transparent,rgb(46,46,46,0.05)_25%,rgba(112,112,112,0.5)_50%,rgba(56,189,248,0.05)_75%,transparent)]"></div>
            {!video && poster && <div ref={imageRef} className="relative h-full w-full overflow-hidden">
                <MotionImage src={poster} alt={title} className="absolute h-full w-[200%] max-w-none object-contain" width={2000} height={2000} quality={85} initial={{
              scale: 1,
              x: 0,
              y: 0
            }} animate={{
              scale: isHovering ? 1.3 : 1,
              x: isHovering ? `${-mousePosition.x * 32}%` : 0,
              y: isHovering ? -mousePosition.y * 2 : 0
            }} transition={{
              duration: 0.2,
              ease: "easeOut"
            }} style={{
              objectPosition: "top left",
              transformOrigin: isHovering ? `${mousePosition.x * 100}% ${mousePosition.y * 100}%` : "top left"
            }} />
                {children}
              </div>}
            {video && <>
                <video ref={playerRef} preload="metadata" loop muted playsInline className="h-full w-full rounded-lg object-cover" poster={poster} onPause={() => setIsPaused(true)} onPlay={() => setIsPaused(false)} onClick={e_0 => {
              const isCurrentlyPaused = e_0.target.paused;
              // only need this for e.g. iOS that autopauses videos
              if (!isCurrentlyPaused) return;
              const isPaused_0 = !isCurrentlyPaused;
              setIsPaused(isPaused_0);
              if (isPaused_0) {
                e_0.target.pause();
              } else {
                e_0.target.play();
              }
            }}>
                  {isInView && <source src={video} type="video/mp4" />}
                </video>
                {children}
                {isPaused && <div className="pointer-events-none absolute inset-0 animate-fadeIn rounded-lg backdrop-blur-sm animate-duration-200">
                    <PauseIcon className="absolute inset-0 m-auto size-16 rounded-full border-[3px] border-primary-500 bg-black/30 p-2.5 text-gray-100 shadow-sm shadow-black/30 backdrop-blur-sm" />
                  </div>}
              </>}
          </div>
          <Line className="line absolute left-1/2 top-full z-[-1] hidden w-[106%] origin-top-left text-neutral-800 group-last:hidden md:block" svgClassName="h-[130px] w-full" />
        </div>
        <div className="-mt-18 relative flex flex-1">
          <GridBackground
        //className="squares [--square-opacity:0.4] [&.overlapping-squares::before]:!animate-none"
        />
          <div className="bg-dotted z-[-2] text-black/80 [background-size:10px_10px]"></div>
          <motion.div className="relative mx-auto flex max-w-md origin-top flex-col items-center justify-center" style={{
          opacity,
          translateY: y_0
        }} ref={containerRef}>
            {Icon}
            <h3 className="mt-6 text-center font-fundra text-2xl font-bold capitalize tracking-normal text-white lg:text-[30px]">
              {title}
            </h3>
            <p className="text-md mx-auto mt-4 inline-flex text-balance px-1 text-center text-neutral-400 sm:mt-4 sm:px-0 lg:text-lg">
              {description}
            </p>
          </motion.div>
        </div>
      </div>
    </div>;
};
const Line = t0 => {
  const $ = _c(33);
  const {
    className,
    svgClassName
  } = t0;
  const id = React.useId();
  const [isGradientHidden, setIsGradientHidden] = useState(true);
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      target: containerRef,
      offset: ["start end", "end start"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    scrollYProgress
  } = useScroll(t1);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [0.1, 0.3];
    t3 = [0, 1];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  const pathLength = useTransform(scrollYProgress, t2, t3);
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [0.099999999, 0.1];
    t5 = [0, 1];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  const opacity = useTransform(scrollYProgress, t4, t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = value => {
      setIsGradientHidden(value < 1);
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  useMotionValueEvent(pathLength, "change", t6);
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      x1: 0,
      x2: 0,
      y1: 0,
      y2: 0
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== isGradientHidden) {
    t8 = isGradientHidden ? "hidden" : {
      x1: [0, 1068],
      x2: [0, 534],
      y1: [0, 134],
      y2: [0, 67]
    };
    $[7] = isGradientHidden;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t10;
  let t11;
  let t12;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      duration: 3,
      repeat: Infinity,
      repeatDelay: 2,
      ease: "easeInOut"
    };
    t10 = <stop stopColor="#10b981" stopOpacity="0" />;
    t11 = <stop stopColor="#10b981" stopOpacity="60%" />;
    t12 = <stop offset="1" stopColor="#10b981" stopOpacity="0" />;
    $[9] = t10;
    $[10] = t11;
    $[11] = t12;
    $[12] = t9;
  } else {
    t10 = $[9];
    t11 = $[10];
    t12 = $[11];
    t9 = $[12];
  }
  let t13;
  if ($[13] !== id || $[14] !== t8) {
    t13 = <defs><motion.linearGradient id={id} gradientUnits="userSpaceOnUse" initial={t7} animate={t8} transition={t9}>{t10}{t11}{t12}</motion.linearGradient></defs>;
    $[13] = id;
    $[14] = t8;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== opacity || $[17] !== pathLength) {
    t14 = <motion.path d="M2 2V55C2 61.6274 7.37258 67 14 67H620C626.627 67 632 72.3726 632 79V132" stroke="currentColor" strokeWidth="1.5" style={{
      pathLength,
      opacity
    }} />;
    $[16] = opacity;
    $[17] = pathLength;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const t15 = !isGradientHidden ? `url(#${id})` : "transparent";
  let t16;
  if ($[19] !== opacity || $[20] !== pathLength) {
    t16 = {
      pathLength,
      opacity
    };
    $[19] = opacity;
    $[20] = pathLength;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t15 || $[23] !== t16) {
    t17 = <motion.path d="M2 2V55C2 61.6274 7.37258 67 14 67H620C626.627 67 632 72.3726 632 79V132" stroke={t15} strokeWidth="1.5" style={t16} />;
    $[22] = t15;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== svgClassName || $[26] !== t13 || $[27] !== t14 || $[28] !== t17) {
    t18 = <svg viewBox="0 0 634 134" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className={svgClassName}>{t13}{t14}{t17}</svg>;
    $[25] = svgClassName;
    $[26] = t13;
    $[27] = t14;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== className || $[31] !== t18) {
    t19 = <div className={className} ref={containerRef}>{t18}</div>;
    $[30] = className;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  return t19;
};
const FeatureTitles = twc.div`-translate-y-6 select-none flex-row items-center justify-center gap-x-2 justify-self-center whitespace-nowrap rounded-full text-xs font-semibold leading-normal text-primary-400 [text-shadow:0_0_4px_rgba(6,78,59,0.6)] md:-translate-y-8 md:gap-x-6 md:text-sm [&_.dot]:text-primary-800`;